/** Four-line collapsible list for action-pending criteria points. */

import { useLayoutEffect, useRef, useState } from "react";

const LINE_HEIGHT_PX = 18;
const COLLAPSED_LINES = 4;
const COLLAPSED_HEIGHT_PX = LINE_HEIGHT_PX * COLLAPSED_LINES;

/** Props for the expandable criteria point list. */
export interface ExpandableCriteriaPointsProps {
  points: string[];
}

/** Render criteria points collapsed to four lines with an inline expansion control. */
export function ExpandableCriteriaPoints({
  points,
}: ExpandableCriteriaPointsProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateOverflow = () => {
      setHasOverflow(content.scrollHeight > COLLAPSED_HEIGHT_PX + 1);
    };

    updateOverflow();
    window.addEventListener("resize", updateOverflow);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateOverflow);
    observer?.observe(content);

    return () => {
      window.removeEventListener("resize", updateOverflow);
      observer?.disconnect();
    };
  }, [points]);

  return (
    <div className="bg-white px-3 pb-3">
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
          isExpanded ? "max-h-none" : "max-h-[4.5rem]"
        }`}
      >
        <div
          ref={contentRef}
          className="flex flex-col gap-[0.38rem] text-xs font-normal"
        >
          {points.map((point, pointIndex) => (
            <div
              key={`${pointIndex}-${point}`}
              className="ml-5 flex items-start gap-2 text-xs leading-[1.125rem] tracking-[0.0125rem] text-[#3D3D3D]"
            >
              <div className="text-xs font-normal text-[#3D3D3D]">&#8226;</div>
              <div>{point}</div>
            </div>
          ))}
        </div>
      </div>

      {hasOverflow ? (
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            className="text-xs font-semibold text-tertiaryBlue-4"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((current) => !current)}
          >
            {isExpanded ? "See less" : "See more"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
